import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import { HTTPError } from 'ky'
import { handleResponse, slowFetch } from '../api/helpers.ts'
import { type CreateUserDto, type UpdateUserDto, type UserEntity, type UserRoleEntity, userManager } from '../api/user-manager.ts'
import { asQueryParam, useImpersonation } from './impersonation.ts'
import { impersonatedQueryData, queryData } from './queryHelpers.ts'

export const userData = impersonatedQueryData((impersonation) =>
	queryOptions({
		queryKey: ['USERS', impersonation?.subscriptionId],
		queryFn: async () => {
			const users = await userManager.GET('/user-manager/users', { params: { query: asQueryParam(impersonation) } }).then(handleResponse)
			const roles = await roleData.ensure()
			return users
				.map((user) => createInsightUser(user, roles))
				.filter((user): user is InsightUser => !!user)
				.toSorted((a, b) => a.fullName.localeCompare(b.fullName))
		},
		staleTime: 1000 * 60,
	}),
)

export const apiKeyData = queryData((userId: string) =>
	queryOptions({
		queryKey: ['API_KEY', userId],
		queryFn: () => userManager.GET('/user-manager/api-keys/service-user/{id}', { params: { path: { id: userId } } }).then(handleResponse),
		retry: 2,
		retryDelay: 1000,
	}),
)

export const roleData = queryData(() =>
	queryOptions({
		queryKey: ['ROLES'],
		queryFn: () =>
			userManager
				.GET('/user-manager/user-roles')
				.then(handleResponse)
				.then((roles) => {
					const user = roles.find((role) => role.name === 'USER')
					const admin = roles.find((role) => role.name === 'ADMIN')
					const superAdmin = roles.find((role) => role.name === 'SUPER_ADMIN')
					const internalReadOnly = roles.find((role) => role.name === 'INTERNAL_READ_ONLY')
					const limitedAdmin = roles.find((role) => role.name === 'LIMITED_ADMIN')
					const foundRoles: Roles = {}
					if (user) foundRoles.user = { ...user, name: 'USER' }
					if (admin) foundRoles.admin = { ...admin, name: 'ADMIN' }
					if (superAdmin) foundRoles.superAdmin = { ...superAdmin, name: 'SUPER_ADMIN' }
					if (internalReadOnly) foundRoles.internalReadOnly = { ...internalReadOnly, name: 'INTERNAL_READ_ONLY' }
					if (limitedAdmin) foundRoles.limitedAdmin = { ...limitedAdmin, name: 'LIMITED_ADMIN' }
					return foundRoles
				}),
		staleTime: 1000 * 60 * 5,
	}),
)

export const useReportsRoles = (): UserRoleEntity['name'][] => {
	return ['ADMIN', 'LIMITED_ADMIN']
}

export type Roles = {
	user?: UserRoleEntity & { name: 'USER' }
	admin?: UserRoleEntity & { name: 'ADMIN' }
	superAdmin?: UserRoleEntity & { name: 'SUPER_ADMIN' }
	internalReadOnly?: UserRoleEntity & { name: 'INTERNAL_READ_ONLY' }
	limitedAdmin?: UserRoleEntity & { name: 'LIMITED_ADMIN' }
}

export type InsightUser = UserEntity & {
	role: UserRoleEntity
	email: { email: string; verified: boolean }
	phone: { phone: string; verified: boolean }
	isAdmin: boolean
	isBlocked: boolean
	isActive: boolean
	isServiceUser: boolean
	isUiUser: boolean
	isSsoUser: boolean
	isVaisalaAdmin: boolean
	isSuperAdmin: boolean
	isInternalReadOnly: boolean
}

export const createInsightUser = (user: UserEntity, roles: Roles): InsightUser | undefined => {
	const role = Object.values(roles).find((role) => user.userRoles.at(0) === role?.id)
	if (!role) {
		console.error(`Role with id ${user.userRoles.at(0)} not found for user ${user.id}`)
		return undefined
	}
	return {
		...user,
		role,
		email: getUserEmail(user),
		phone: getUserPhone(user),
		isAdmin: role.name === 'ADMIN',
		isBlocked: user.status === 'Blocked',
		isActive: user.status === 'Active',
		isServiceUser: user.subType === 'ServiceUser',
		isUiUser: user.subType === 'UIUser',
		isSsoUser: isSsoUser(user),
		isVaisalaAdmin: role.name === 'SUPER_ADMIN' || role.name === 'INTERNAL_READ_ONLY',
		isSuperAdmin: role.name === 'SUPER_ADMIN',
		isInternalReadOnly: role.name === 'INTERNAL_READ_ONLY',
	}
}

const getUserEmail = (user: UserEntity) => {
	const email = user.contactMedia.find(({ mediumType }) => mediumType === 'Email')
	return { email: email?.characteristic.name ?? '', verified: email?.isVerified ?? false }
}

const getUserPhone = (user: UserEntity) => {
	const phone = user.contactMedia.find(({ mediumType }) => mediumType === 'Phone')
	return { phone: phone?.characteristic.name ?? '', verified: phone?.isVerified ?? false }
}

const isSsoUser = (user: UserEntity): boolean => {
	const authProviderUserId = user.externalReferences?.find((ref) => ref.provider === 'Auth0')?.id
	if (!authProviderUserId) return false
	return !authProviderUserId.startsWith('auth0|') // Non-SSO user id prefix, SSO prefixes: waad|, google-apps| etc
}

export const useCheckEmail = () =>
	useMutation({
		mutationFn: (email: string) =>
			userManager.GET('/user-manager/users/user-exists', { params: { query: { email } } }).then(handleResponse),
	})

export const useUserSearch = ({ email }: { email: string }) =>
	useQuery({
		queryKey: ['USER_SEARCH', email],
		queryFn: () => userManager.GET('/user-manager/users/search', { params: { query: { email } } }).then(handleResponse),
		enabled: !!email,
	})

// Mutations

export const useCreateUser = () => {
	const impersonation = useImpersonation()
	return useMutation<UserEntity, HTTPError, CreateUserDto>({
		mutationFn: (body) =>
			userManager
				.POST('/user-manager/users', { params: { query: asQueryParam(impersonation) }, body })
				.then(handleResponse)
				.catch(async (e) => {
					if (e instanceof HTTPError) throw await e.response.json<{ message: string }>()
					throw e
				}),
		onSuccess: () => userData.invalidate(impersonation),
	})
}

type CreateUsersErrorResponse = {
	statusCode: number
	error: string
	message: string
	emails?: string[]
}

export const useCreateUsers = () => {
	const impersonation = useImpersonation()
	return useMutation<UserEntity, CreateUsersErrorResponse, CreateUserDto[]>({
		mutationFn: (body) =>
			userManager
				.POST('/user-manager/users/batch/create', {
					params: { query: asQueryParam(impersonation) },
					body: { subType: 'UIUser', data: body },
				})
				.then(handleResponse)
				.catch(async (e) => {
					if (e instanceof HTTPError) throw await e.response.json<CreateUsersErrorResponse>()
					throw e
				}),
		onSuccess: () => userData.invalidate(impersonation),
	})
}

export const useUpdateUser = () => {
	const impersonation = useImpersonation()
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: UpdateUserDto }) =>
			userManager.PATCH('/user-manager/users/{id}', {
				params: { path: { id }, query: asQueryParam(impersonation) },
				body,
			}),
		onSuccess: () => userData.invalidate(impersonation),
	})
}

export const useDeleteUser = () => {
	const impersonation = useImpersonation()
	return useMutation({
		mutationFn: (id: string) =>
			userManager.DELETE('/user-manager/users/{id}', {
				params: { path: { id }, query: asQueryParam(impersonation) },
				fetch: slowFetch, // Deleting a user can take even 20 seconds
			}),
		onSuccess: () => userData.invalidate(impersonation),
	})
}

export const useResetPassword = () =>
	useMutation({ mutationFn: (id: string) => userManager.POST('/user-manager/auth/password-request', { body: { userToReset: id } }) })
export const useResendEmailVerification = () =>
	useMutation({ mutationFn: (id: string) => userManager.POST('/user-manager/users/verification/email', { body: { userId: id } }) })
export const useEnableMFA = () => {
	const impersonation = useImpersonation()
	return useMutation({
		mutationFn: (id: string) => userManager.POST('/user-manager/auth/mfa', { body: { userToEnroll: id } }),
		onSuccess: () => userData.invalidate(impersonation),
	})
}
export const useDisableMFA = () => {
	const impersonation = useImpersonation()
	return useMutation({
		mutationFn: (id: string) => userManager.DELETE('/user-manager/auth/mfa/{userId}', { params: { path: { userId: id } } }),
		onSuccess: () => userData.invalidate(impersonation),
	})
}
